import {
  Container,
  Graphics,
  Point,
  Text,
  type FederatedPointerEvent,
  type PointData
} from 'pixi.js';
import { UI_COLORS, UI_FONT_FAMILY } from './Theme';

export type VolumeSliderChannel = 'master' | 'bgm' | 'se' | 'voice';

export interface VolumeSliderOptions {
  channel: VolumeSliderChannel;
  label?: string;
  value?: number;
  width?: number;
  disabled?: boolean;
  onChange?: (value: number, channel: VolumeSliderChannel) => void;
}

export interface VolumeSliderState {
  channel: VolumeSliderChannel;
  value: number;
  disabled: boolean;
}

const CHANNEL_LABELS: Record<VolumeSliderChannel, string> = {
  master: 'Master',
  bgm: 'BGM',
  se: 'SE',
  voice: 'Voice'
};

export class VolumeSlider extends Container {
  private readonly labelText = new Text({
    text: '',
    style: {
      fill: UI_COLORS.ink,
      fontFamily: UI_FONT_FAMILY,
      fontSize: 22,
      fontWeight: '700'
    }
  });
  private readonly valueText = new Text({
    text: '',
    style: {
      fill: UI_COLORS.gold,
      fontFamily: UI_FONT_FAMILY,
      fontSize: 20,
      fontWeight: '700'
    }
  });
  private readonly track = new Graphics();
  private readonly fill = new Graphics();
  private readonly thumb = new Graphics();
  private readonly hit = new Graphics();
  private readonly localPoint = new Point();

  private channel: VolumeSliderChannel;
  private sliderWidth: number;
  private value: number;
  private disabled = false;
  private dragging = false;
  private onChange?: (value: number, channel: VolumeSliderChannel) => void;

  public constructor(options: VolumeSliderOptions) {
    super();
    this.channel = options.channel;
    this.sliderWidth = normalizeWidth(options.width);
    this.value = clamp01(options.value ?? 1);
    this.disabled = options.disabled ?? false;
    this.onChange = options.onChange;

    this.addChild(this.labelText);
    this.addChild(this.track);
    this.addChild(this.fill);
    this.addChild(this.thumb);
    this.addChild(this.valueText);
    this.addChild(this.hit);

    this.applyDisabledState();
    this.hit.on('pointerdown', this.handlePointerDown);
    this.hit.on('pointermove', this.handlePointerMove);
    this.hit.on('pointerup', this.handlePointerUp);
    this.hit.on('pointerupoutside', this.handlePointerUp);
    this.hit.on('pointercancel', this.handlePointerUp);

    this.setLabel(options.label ?? CHANNEL_LABELS[this.channel]);
    this.draw();
  }

  public setValue(value: number, notify = false): void {
    const nextValue = clamp01(value);
    if (nextValue === this.value) {
      return;
    }

    this.value = nextValue;
    this.draw();

    if (notify) {
      this.onChange?.(this.value, this.channel);
    }
  }

  public setDisabled(disabled: boolean): void {
    this.disabled = disabled;
    this.dragging = false;
    this.applyDisabledState();
  }

  public setOnChange(
    onChange: ((value: number, channel: VolumeSliderChannel) => void) | undefined
  ): void {
    this.onChange = onChange;
  }

  public setWidth(width: number): void {
    this.sliderWidth = normalizeWidth(width);
    this.draw();
  }

  public getState(): VolumeSliderState {
    return {
      channel: this.channel,
      value: this.value,
      disabled: this.disabled
    };
  }

  private setLabel(label: string): void {
    this.labelText.text = label;
  }

  private applyDisabledState(): void {
    this.hit.eventMode = this.disabled ? 'none' : 'static';
    this.hit.cursor = this.disabled ? 'default' : 'pointer';
    this.alpha = this.disabled ? 0.55 : 1;
  }

  private readonly handlePointerDown = (event: FederatedPointerEvent): void => {
    if (this.disabled) {
      return;
    }

    event.stopPropagation();
    this.dragging = true;
    this.updateValueFromEvent(event);
  };

  private readonly handlePointerMove = (event: FederatedPointerEvent): void => {
    if (!this.dragging || this.disabled) {
      return;
    }

    event.stopPropagation();
    this.updateValueFromEvent(event);
  };

  private readonly handlePointerUp = (event: FederatedPointerEvent): void => {
    if (!this.dragging) {
      return;
    }

    event.stopPropagation();
    this.dragging = false;
  };

  private updateValueFromEvent(event: FederatedPointerEvent): void {
    const local = this.toLocal(event.global as PointData, undefined, this.localPoint);
    const trackX = getTrackX();
    this.setValue((local.x - trackX) / this.sliderWidth, true);
  }

  private draw(): void {
    const trackX = getTrackX();
    const trackY = 34;
    const trackHeight = 8;
    const thumbX = trackX + this.sliderWidth * this.value;

    this.labelText.position.set(0, 18);
    this.valueText.text = `${Math.round(this.value * 100)}%`;
    this.valueText.position.set(trackX + this.sliderWidth + 24, 18);

    this.track.clear();
    this.track
      .roundRect(trackX, trackY, this.sliderWidth, trackHeight, 4)
      .fill({ color: UI_COLORS.paperLight, alpha: 0.9 })
      .stroke({ color: UI_COLORS.gold, width: 1, alpha: 0.44 });

    this.fill.clear();
    this.fill
      .roundRect(trackX, trackY, Math.max(0, thumbX - trackX), trackHeight, 4)
      .fill({ color: UI_COLORS.gold, alpha: 0.86 });

    this.thumb.clear();
    this.thumb
      .circle(thumbX, trackY + trackHeight / 2, 13)
      .fill({ color: UI_COLORS.ink, alpha: 0.96 })
      .stroke({ color: UI_COLORS.red, width: 3, alpha: 0.9 });

    this.hit.clear();
    this.hit.rect(trackX - 16, trackY - 18, this.sliderWidth + 32, 44).fill({
      color: UI_COLORS.black,
      alpha: 0.001
    });
  }
}

function getTrackX(): number {
  return 118;
}

function clamp01(value: number): number {
  if (!Number.isFinite(value)) {
    return 1;
  }

  return Math.min(Math.max(value, 0), 1);
}

function normalizeWidth(width: number | undefined): number {
  if (width === undefined || !Number.isFinite(width)) {
    return 420;
  }

  return Math.max(220, width);
}
